import React from 'react';
import 'twin.macro';
import { Attachment } from 'types/attachment';
import AttachmentsComponent from 'components/Attachments/ButtonAttachment';
import { useUpdateCJEventAttachment } from 'containers/CahierJournal/queries/useUploadCJEventAttachment';
import useNotification from 'containers/App/Notifications/useNotification';
import Popover from 'components/Popover';
import { Tag } from 'components/Tag';
import Button from 'components/Buttons/Button';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/20/solid';
import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { InputAttachment } from 'components/Attachments/InputAttachment';
import { inputRootStyle } from 'components/Form/Input2';
import tw from 'twin.macro';

interface Props {
  value?: Attachment[];
  onChange: (v: Attachment[]) => void;
  minimal?: boolean;
  small?: boolean;
  readonly?: boolean;
}

export function FilesAndMediaCell(props: Props) {
  const [attachments, setAttachments] = React.useState<Attachment[]>(
    props.value || [],
  );
  const { upload, isUploading } = useUpdateCJEventAttachment();
  const { t } = useTranslation(['common']);
  const { notifyError } = useNotification();
  const onChange = (v: Attachment[]) => {
    setAttachments(v);
    props.onChange(v);
  };
  if (attachments.length === 0) {
    if (props.readonly) {
      return null;
    }
    return (
      <InputAttachment
        minimal={props.minimal}
        onChange={onChange}
        attachments={attachments}
        uploadingAttachment={isUploading}
        handleError={({ message }) => {
          notifyError({
            title: message,
          });
        }}
        multiple
        handleUploadAttachment={({ file }) =>
          upload({ file })
            .then(v => v)
            .catch(() => {
              notifyError({
                title: t('common:messages.problemUploadingFile'),
              });
            })
        }
      />
    );
  }

  const attachmentsComponents = (
    <>
      {attachments.map(v => (
        <div tw="flex max-w-[18rem]">
          <Tag
            size="sm"
            key={v.fileUrl}
            icon={PaperClipIcon}
            onClick={e => {
              e.stopPropagation();
              window.open(v.fileUrl);
            }}
            // Le h-18px et 24px c'est pour que ça ait la même hauteur qu'un input classique
            css={[
              props.small ? tw`h-[18px]` : tw`h-[24px]`,
              tw`cursor-zoom-in min-w-0`,
            ]}
          >
            <span tw="truncate">{v.label}</span>
          </Tag>
        </div>
      ))}
    </>
  );

  if (props.readonly) {
    return <>{attachmentsComponents}</>;
  }
  return (
    <Popover portal fill placement="bottom-start">
      <Popover.Span>
        <div
          css={[
            inputRootStyle({}),
            tw`pl-2 py-1.5 cursor-pointer flex flex-col justify-center gap-1`,
            props.minimal && tw`border-transparent hover:border-neutral-quiet`,
          ]}
        >
          {attachmentsComponents}
        </div>
      </Popover.Span>
      <Popover.Panel size="auto">
        <div tw="mb-2">
          {attachments.map(v => (
            <div
              tw="pl-4 flex justify-between items-center max-w-xl gap-x-2"
              key={v.fileUrl}
            >
              <div tw="text-xs truncate">{v.label}</div>
              <div>
                <Button
                  icon={ArrowDownTrayIcon}
                  minimal
                  small
                  iconSize="sm"
                  onClick={() => window.open(v.fileUrl)}
                />
                <Button
                  icon={TrashIcon}
                  minimal
                  small
                  iconSize="sm"
                  onClick={() =>
                    onChange(
                      (attachments || []).filter(
                        v2 => v2.fileUrl !== v.fileUrl,
                      ),
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>
        <AttachmentsComponent
          onChange={onChange}
          attachments={attachments}
          fill
          alignText="left"
          label={t('common:customFields.actions.addAFile')}
          icon={PlusIcon}
          uploadingAttachment={isUploading}
          handleError={({ message }) => {
            notifyError({
              title: message,
            });
          }}
          multiple
          minimal
          handleUploadAttachment={({ file }) =>
            upload({ file })
              .then(v => v)
              .catch(() => {
                notifyError({
                  title: t('common:messages.problemUploadingFile'),
                });
              })
          }
        />
      </Popover.Panel>
    </Popover>
  );
}
